import { useState, useEffect } from 'react';
import { useAuthStore, useThemeStore } from '../store';
import { api } from '../lib/api';
import { base64ToUint8Array, arrayBufferToBase64url } from '../lib/utils';

export default function SettingsPage() {
  const { user, setUser, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'privacy' | 'appearance' | 'subscription'>('profile');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [emailInput, setEmailInput] = useState(user?.email || '');
  const [emailStatus, setEmailStatus] = useState('');
  const [emailStatusType, setEmailStatusType] = useState<'success' | 'error' | ''>('');
  const [emailUpdating, setEmailUpdating] = useState(false);
  const [passkeyStatus, setPasskeyStatus] = useState('');
  const [passkeyStatusType, setPasskeyStatusType] = useState<'success' | 'error' | ''>('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState<boolean>(!!user?.hasPassword);
  const [hasPasskey, setHasPasskey] = useState<boolean>(!!user?.hasPasskey);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState('');
  const [passwordStatusType, setPasswordStatusType] = useState<'success' | 'error' | ''>('');
  const supportsPasskeys = typeof window !== 'undefined' && typeof navigator !== 'undefined' && typeof navigator.credentials?.create === 'function';
  
  // Profile form
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [handle, setHandle] = useState(user?.handle || '');
  
  // Notification settings
  const [notifSettings, setNotifSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    newFollower: true,
    newMessage: true,
    postReaction: true,
    postComment: true,
    mention: true,
  });
  
  useEffect(() => {
    // Load notification settings
    const loadSettings = async () => {
      try {
        const settings = await api.notifications.getPreferences();
        setNotifSettings(settings);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    setEmailInput(user?.email || '');
    setHasPassword(!!user?.hasPassword);
    setHasPasskey(!!user?.hasPasskey);
  }, [user?.email, user?.hasPassword, user?.hasPasskey]);

  const loadSecurityStatus = async () => {
    if (!user) {
      return;
    }
    setSecurityLoading(true);
    try {
      const status = await api.auth.getSecurityStatus();
      setHasPassword(status.hasPassword);
      setHasPasskey(status.hasPasskey);
      setUser({ ...user, hasPassword: status.hasPassword, hasPasskey: status.hasPasskey });
    } catch (err) {
      console.error('Failed to load security status:', err);
    } finally {
      setSecurityLoading(false);
    }
  };

  useEffect(() => {
    void loadSecurityStatus();
  }, [user?.id]);
  
  const handleProfileSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const updated = await api.users.update({
        displayName,
        bio,
        handle,
      });
      setUser(updated);
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNotificationsSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await api.notifications.updatePreferences(notifSettings);
      setSuccess('Notification settings updated');
    } catch (err: any) {
      setError(err.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    setEmailStatus('');
    setEmailStatusType('');
    if (!user) {
      setEmailStatusType('error');
      setEmailStatus('You must be signed in to update your email');
      return;
    }

    if (!emailInput.trim()) {
      setEmailStatusType('error');
      setEmailStatus('Email is required');
      return;
    }

    setEmailUpdating(true);
    try {
      const updatedEmail = await api.auth.updateEmail(emailInput.trim());
      setUser({ ...user, email: updatedEmail });
      setEmailStatusType('success');
      setEmailStatus('Login email updated');
    } catch (err: any) {
      setEmailStatusType('error');
      setEmailStatus(err.message || 'Failed to update email');
    } finally {
      setEmailUpdating(false);
    }
  };

  const handleAddPasskey = async () => {
    setPasskeyStatus('');
    setPasskeyStatusType('');
    if (!supportsPasskeys) {
      setPasskeyStatusType('error');
      setPasskeyStatus('Your browser does not support passkeys');
      return;
    }
    if (!user?.email) {
      setPasskeyStatusType('error');
      setPasskeyStatus('Email is required to set up a passkey');
      return;
    }

    setPasskeyLoading(true);
    try {
      const options = await api.auth.getPasskeyRegistrationOptions(user.email);
      const creationOptions = {
        challenge: base64ToUint8Array(options.challenge),
        rp: options.rp,
        user: {
          id: base64ToUint8Array(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        attestation: options.attestation,
        authenticatorSelection: options.authenticatorSelection,
        excludeCredentials: (options.excludeCredentials || []).map((cred: any) => ({
          id: base64ToUint8Array(cred.id),
          type: cred.type,
          transports: cred.transports,
        })),
      } as PublicKeyCredentialCreationOptions;

      const credential = await navigator.credentials.create({ publicKey: creationOptions }) as PublicKeyCredential;
      if (!credential) {
        throw new Error('Passkey creation cancelled');
      }

      const response = credential.response as AuthenticatorAttestationResponse;
      const publicKey = typeof response.getPublicKey === 'function' ? response.getPublicKey() : null;
      if (!publicKey) {
        throw new Error('Unable to read public key from authenticator');
      }

      await api.auth.registerPasskey({
        email: user.email,
        credentialId: arrayBufferToBase64url(credential.rawId),
        publicKey: arrayBufferToBase64url(publicKey),
        attestationObject: arrayBufferToBase64url(response.attestationObject),
        clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      });

      setPasskeyStatusType('success');
      setPasskeyStatus('Passkey added successfully');
      setHasPasskey(true);
      if (user) {
        setUser({ ...user, hasPasskey: true });
      }
    } catch (err: any) {
      const message = err.name === 'NotAllowedError' ? 'Passkey setup was cancelled' : (err.message || 'Failed to add passkey');
      setPasskeyStatusType('error');
      setPasskeyStatus(message);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    setPasswordStatus('');
    setPasswordStatusType('');

    if (!newPassword) {
      setPasswordStatusType('error');
      setPasswordStatus('New password is required');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordStatusType('error');
      setPasswordStatus('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatusType('error');
      setPasswordStatus('Passwords do not match');
      return;
    }

    setSecurityLoading(true);
    try {
      await api.auth.updatePassword(newPassword, currentPassword || undefined);
      setHasPassword(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordStatusType('success');
      setPasswordStatus(hasPassword ? 'Password updated' : 'Password added');
      if (user) {
        setUser({ ...user, hasPassword: true });
      }
    } catch (err: any) {
      setPasswordStatusType('error');
      setPasswordStatus(err.message || 'Failed to update password');
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleRemovePassword = async () => {
    setPasswordStatus('');
    setPasswordStatusType('');

    if (!hasPasskey) {
      setPasswordStatusType('error');
      setPasswordStatus('Add a passkey before removing your password');
      return;
    }

    setSecurityLoading(true);
    try {
      await api.auth.removePassword();
      setHasPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordStatusType('success');
      setPasswordStatus('Password removed');
      if (user) {
        setUser({ ...user, hasPassword: false });
      }
    } catch (err: any) {
      setPasswordStatusType('error');
      setPasswordStatus(err.message || 'Failed to remove password');
    } finally {
      setSecurityLoading(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    if (!confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.users.deleteAccount();
      logout();
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Settings</h1>
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <nav className="lg:w-48 flex-shrink-0">
          <div className="flex lg:flex-col gap-1 overflow-x-auto">
            {[
              { id: 'profile', label: 'Profile', icon: UserIcon },
              { id: 'notifications', label: 'Notifications', icon: BellIcon },
              { id: 'privacy', label: 'Privacy', icon: LockIcon },
              { id: 'appearance', label: 'Appearance', icon: PaletteIcon },
              { id: 'subscription', label: 'Subscription', icon: StarIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </nav>
        
        {/* Content */}
        <div className="flex-1">
          {success && (
            <div className="bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm mb-4">
              {success}
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}
          
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Profile Settings</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Handle
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">@</span>
                    <input
                      type="text"
                      value={handle}
                      onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      className="input pl-8"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Bio
                  </label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="input min-h-[100px]"
                    maxLength={160}
                  />
                  <p className="mt-1 text-xs text-slate-500">{bio.length}/160</p>
                </div>
                
                <button
                  onClick={handleProfileSave}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              <div className="mt-8 space-y-6 border-t border-slate-200 dark:border-slate-700 pt-6">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Login email</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Update the address you use to sign in.</p>
                    </div>
                    <button
                      onClick={handleEmailChange}
                      disabled={emailUpdating}
                      className="btn btn-secondary"
                    >
                      {emailUpdating ? 'Updating...' : 'Update email'}
                    </button>
                  </div>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    className="input"
                    placeholder="name@example.com"
                  />
                  {emailStatus && (
                    <p className={`text-xs ${emailStatusType === 'error' ? 'text-red-600' : 'text-green-600 dark:text-green-300'}`}>
                      {emailStatus}
                    </p>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Password</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {hasPassword ? 'Change your account password.' : 'Set a password for email sign-in.'}
                        </p>
                      </div>
                      <button
                        onClick={handlePasswordSave}
                        disabled={securityLoading}
                        className="btn btn-secondary"
                      >
                        {securityLoading ? 'Saving...' : (hasPassword ? 'Change password' : 'Add password')}
                      </button>
                    </div>
                    {hasPassword && (
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="input"
                        placeholder="Current password (optional)"
                        autoComplete="current-password"
                      />
                    )}
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="input"
                      placeholder="New password"
                      autoComplete="new-password"
                    />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                    {hasPassword && (
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          You can remove your password only when a passkey is set.
                        </p>
                        <button
                          onClick={handleRemovePassword}
                          disabled={securityLoading || !hasPasskey}
                          className="btn btn-secondary"
                        >
                          Remove password
                        </button>
                      </div>
                    )}
                    {passwordStatus && (
                      <p className={`text-xs ${passwordStatusType === 'error' ? 'text-red-600' : 'text-green-600 dark:text-green-300'}`}>
                        {passwordStatus}
                      </p>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Passkeys</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Add a passkey for passwordless sign-in.</p>
                    </div>
                    <button
                      onClick={handleAddPasskey}
                      disabled={passkeyLoading || !supportsPasskeys}
                      className="btn btn-secondary"
                    >
                      {passkeyLoading ? 'Adding...' : 'Add passkey'}
                    </button>
                  </div>
                  {!supportsPasskeys && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Passkeys require a WebAuthn-compatible browser (Chrome, Edge, Safari 15+, etc.).
                    </p>
                  )}
                  {passkeyStatus && (
                    <p className={`text-xs ${passkeyStatusType === 'error' ? 'text-red-600' : 'text-green-600 dark:text-green-300'}`}>
                      {passkeyStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Notification Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Email Notifications</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Receive notifications via email</p>
                  </div>
                  <Toggle
                    checked={notifSettings.emailNotifications}
                    onChange={(checked) => setNotifSettings({ ...notifSettings, emailNotifications: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Push Notifications</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Receive push notifications in browser</p>
                  </div>
                  <Toggle
                    checked={notifSettings.pushNotifications}
                    onChange={(checked) => setNotifSettings({ ...notifSettings, pushNotifications: checked })}
                  />
                </div>
                
                <hr className="border-slate-200 dark:border-slate-700" />
                
                {[
                  { key: 'newFollower', label: 'New Follower', desc: 'When someone follows you' },
                  { key: 'newMessage', label: 'New Message', desc: 'When you receive a message' },
                  { key: 'postReaction', label: 'Post Reactions', desc: 'When someone reacts to your post' },
                  { key: 'postComment', label: 'Post Comments', desc: 'When someone comments on your post' },
                  { key: 'mention', label: 'Mentions', desc: 'When someone mentions you' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{item.label}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{item.desc}</p>
                    </div>
                    <Toggle
                      checked={notifSettings[item.key as keyof typeof notifSettings] as boolean}
                      onChange={(checked) => setNotifSettings({ ...notifSettings, [item.key]: checked })}
                    />
                  </div>
                ))}
                
                <button
                  onClick={handleNotificationsSave}
                  disabled={loading}
                  className="btn btn-primary mt-4"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
          
          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div className="card p-6">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Privacy Settings</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Show Location on Posts</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Display canal segment on your posts</p>
                    </div>
                    <Toggle checked={true} onChange={() => {}} />
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Allow Direct Messages</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Let other users message you</p>
                    </div>
                    <Toggle checked={true} onChange={() => {}} />
                  </div>
                </div>
              </div>
              
              <div className="card p-6 border-red-200 dark:border-red-800">
                <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Danger Zone</h2>
                
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                
                <button
                  onClick={handleDeleteAccount}
                  className="btn bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Account
                </button>
              </div>
            </div>
          )}
          
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Appearance</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    Theme
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {(['light', 'dark', 'auto'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setTheme(t)}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          theme === t
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <div className="mb-2">
                          {t === 'light' && <SunIcon className="w-6 h-6 mx-auto text-yellow-500" />}
                          {t === 'dark' && <MoonIcon className="w-6 h-6 mx-auto text-slate-400" />}
                          {t === 'auto' && <ComputerIcon className="w-6 h-6 mx-auto text-slate-500" />}
                        </div>
                        <span className="text-sm font-medium text-slate-900 dark:text-white capitalize">
                          {t === 'auto' ? 'System' : t}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Subscription</h2>
              
              {user?.isSubscribed ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <StarIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">Moorly Pro</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Active subscription</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Your subscription renews on <span className="font-medium">January 1, 2026</span>
                    </p>
                  </div>
                  
                  <button className="btn btn-secondary">Manage Subscription</button>
                </div>
              ) : (
                <div>
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-6 text-white mb-6">
                    <h3 className="text-xl font-bold mb-2">Go Ad-Free with Moorly Pro</h3>
                    <p className="text-primary-100 mb-4">
                      Enjoy Moorly without any ads for just £19.99/year
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-5 h-5" />
                        <span>No banner ads</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-5 h-5" />
                        <span>No native ads in feed</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-5 h-5" />
                        <span>Support independent development</span>
                      </li>
                    </ul>
                    <button className="w-full bg-white text-primary-600 font-semibold py-3 rounded-lg hover:bg-primary-50 transition-colors">
                      Subscribe for £19.99/year
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        checked ? 'bg-primary-500' : 'bg-slate-300 dark:bg-slate-600'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-5' : ''
        }`}
      />
    </button>
  );
}

// Icons
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function PaletteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}

function ComputerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
